import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Formik } from 'formik';
import * as yup from 'yup';
import { 
    useTheme, 
    useMediaQuery, 
    Box, 
    Typography, 
    TextField, 
    Button, 
    CircularProgress, 
    FormControlLabel, 
    Checkbox, 
    IconButton, 
    List, 
    ListItem, 
    Select, 
    MenuItem, 
    FormControl, 
    InputLabel 
} from '@mui/material';
import {
    Add,
    Delete,
    ArrowBackIosNew,
} from '@mui/icons-material';
import useAuthCheck from 'hooks/useAuthCheck';
import { LAYOUT_HEIGHT } from 'state/constants';
import api from 'state/axios';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import Header from 'components/Header';
import StyledButton from 'components/StyledButton';


const AircraftPage = () => {
    useAuthCheck();
    const { aircraftId } = useParams();    
    const ranks = useSelector((state) => state.crewRanks);
    const navigate = useNavigate();    
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const [aircraft, setAircraft] = useState({});

    const initialValues = {
        acTypeCode: aircraft.acTypeCode || '',
        acReg: aircraft.acReg || '',
        serialNo: aircraft.serialNo || '',
        countryCode: aircraft.countryCode || '',
        capacity: aircraft.capacity || 180,
        usePound: aircraft.usePound || '1',
        crewComplement: aircraft.crewComplement || [{ rankId: '', quantity: 0, isCompulsory: false }],
        isActive: aircraft.isActive !== undefined ? aircraft.isActive : true,
    };
                
    useEffect(() => {
        if (!aircraftId) return;

        const fetchData = async () => {
            setLoading(true);
            try {
                const { data } = await api.get(`/aircrafts/${aircraftId}`);
                setAircraft(data);
            } catch (err) {
                setError(err);
                toast.error(err?.response?.data?.message || 'Failed to fetch aircraft!');
            } finally {
                setLoading(false);
            }
        };
        
        fetchData();
    }, [aircraftId]);
    
    const checkoutSchema = yup.object().shape({
        acTypeCode: yup.string().required('Required'),
        acReg: yup.string().required('Required'),
    });

    const handleFormSubmit = async (values, onSubmitProps) => {
        try {
            const method = aircraftId ? 'PUT' : 'POST';
            const url = aircraftId
                ? `/aircrafts/update/${aircraftId}`
                : '/aircrafts/new';
        
            await api({
                method,
                url,
                data: values,
            });
    
            toast.success(aircraftId 
                ? 'Aircraft updated successfully.' 
                : 'Aircraft created successfully.'
            );

            if (!aircraftId) {
                onSubmitProps.resetForm();
            }

            navigate("/aircrafts");
        } catch (error) {
            toast.error(error?.message || 'Failed to submit the form!');
            setError(error);
        }
    };

    const addCrewMember = (values, setFieldValue) => {
        setFieldValue('crewComplement', [...values.crewComplement, { rankId: '', quantity: 1, isCompulsory: false }]);
    };
    
    const removeCrewMember = (values, setFieldValue, index) => {
        const newCrewRank = values.crewComplement.filter((_, i) => i !== index);
        setFieldValue('crewComplement', newCrewRank);
    };

    if (loading) return (
        <FlexCenter height={LAYOUT_HEIGHT}>
            <CircularProgress />
        </FlexCenter>
    );

    if (error) return (
        <FlexCenter height={LAYOUT_HEIGHT}>
            <Typography color="error">{error?.message}</Typography>
        </FlexCenter>
    );


    return (
        <Box className="main-container-box">                
            <FlexBetween>
                <Header
                    title="Aircraft"
                    subtitle={`${loading ? 'Loading...' : aircraftId ? 'Update' : 'Create'}`}
                />
                {!loading && (
                    <Box display="flex" alignItems="center" gap={1}>
                        <StyledButton 
                            onClick={() => navigate("/aircrafts")}
                        >
                            <ArrowBackIosNew />
                        </StyledButton>
                    </Box>
                )}
            </FlexBetween>            
            <Box height={LAYOUT_HEIGHT} sx={{ overflowY: 'auto' }}>
                <Formik
                    initialValues={initialValues}
                    enableReinitialize={true}
                    validationSchema={checkoutSchema}
                    onSubmit={handleFormSubmit}
                >
                    {({
                        values,
                        errors,
                        touched,
                        handleBlur,
                        handleChange,
                        handleSubmit,
                        setFieldValue,
                    }) => (
                        <form onSubmit={handleSubmit}>
                            <Box
                                display="grid"
                                gridTemplateColumns="repeat(12, minmax(0, 1fr))"
                                gap={2}
                            >
                                <TextField
                                    required
                                    fullWidth
                                    variant="filled"
                                    type="text"
                                    label="AC Type Code"
                                    onBlur={handleBlur}
                                    onChange={handleChange}
                                    value={values.acTypeCode}
                                    name="acTypeCode"
                                    error={!!touched.aircraftCode && !!errors.acTypeCode}
                                    helperText={touched.acTypeCode && errors.acTypeCode}
                                    sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}
                                />

                                <TextField
                                    required
                                    fullWidth
                                    variant="filled"
                                    type="text"
                                    label="AC Registration"
                                    onBlur={handleBlur}
                                    onChange={handleChange}
                                    value={values.acReg}
                                    name="acReg"
                                    error={!!touched.acReg && !!errors.acReg}
                                    helperText={touched.acReg && errors.acReg}
                                    sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}
                                />

                                <TextField
                                    fullWidth
                                    variant="filled"
                                    type="text"
                                    label="Serial Number"
                                    onBlur={handleBlur}
                                    onChange={handleChange}
                                    value={values.serialNo}
                                    name="serialNo"
                                    error={!!touched.serialNo && !!errors.serialNo}
                                    helperText={touched.serialNo && errors.serialNo}
                                    sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}
                                />

                                <TextField
                                    fullWidth
                                    variant="filled"
                                    type="text"
                                    label="Country Code"
                                    onBlur={handleBlur}
                                    onChange={handleChange}
                                    value={values.countryCode}
                                    name="countryCode"
                                    error={!!touched.countryCode && !!errors.countryCode}
                                    helperText={touched.countryCode && errors.countryCode}
                                    sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}
                                />

                                <TextField
                                    fullWidth
                                    required
                                    variant="filled"
                                    type="number"
                                    label="Capacity"
                                    onBlur={handleBlur}
                                    onChange={handleChange}
                                    value={values.capacity}
                                    name="capacity"
                                    error={!!touched.capacity && !!errors.capacity}
                                    helperText={touched.capacity && errors.capacity}
                                    sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}
                                />

                                <TextField
                                    fullWidth
                                    variant="filled"
                                    type="text"
                                    label="Use Pound"
                                    onBlur={handleBlur}
                                    onChange={handleChange}
                                    value={values.usePound}
                                    name="usePound"
                                    error={!!touched.usePound && !!errors.usePound}
                                    helperText={touched.usePound && errors.usePound}
                                    sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}
                                />

                                <Typography variant="h6" fontWeight="bold" sx={{ gridColumn: 'span 12' }}>
                                    Crew Complement
                                </Typography>

                                <List sx={{ gridColumn: 'span 12' }}>
                                    {values.crewComplement.map((crew, index) => (
                                        <ListItem key={index} secondaryAction={
                                            <IconButton 
                                                aria-label="delete" 
                                                edge="end" 
                                                onClick={() => removeCrewMember(values, setFieldValue, index)}
                                            >
                                                <Delete />
                                            </IconButton>
                                        }>
                                            <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} gap={1} width="100%">
                                                <FormControl fullWidth variant="filled" sx={{ minWidth: isMobile ? '20%' : '30%' }}>
                                                    <InputLabel id={`rank-label-${index}`}>Rank {index + 1}</InputLabel>
                                                    <Select
                                                        labelId={`rank-label-${index}`}
                                                        label={`Rank ID ${index + 1}`}
                                                        id={`rank-select-${index}`}
                                                        name={`crewComplement[${index}].rankId`}
                                                        value={crew.rankId}
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        error={touched.crewComplement?.[index]?.rankId && !!errors.crewComplement?.[index]?.rankId}
                                                    >
                                                        {ranks.map((rank) => (
                                                            <MenuItem key={rank._id} value={rank._id}>
                                                                {rank.rankCode} 
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                    {touched.crewComplement?.[index]?.rankId && errors.crewComplement?.[index]?.rankId && (
                                                        <Typography color="error" variant="caption">
                                                            {errors.crewComplement[index].rankId}
                                                        </Typography>
                                                    )}
                                                </FormControl>

                                                <TextField
                                                    fullWidth
                                                    variant="filled"
                                                    type="number"
                                                    label={`Quantity ${index + 1}`}
                                                    name={`crewComplement[${index}].quantity`}
                                                    value={crew.quantity}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    error={touched.crewComplement?.[index]?.quantity && !!errors.crewComplement?.[index]?.quantity}
                                                    helperText={touched.crewComplement?.[index]?.quantity && errors.crewComplement?.[index]?.quantity}
                                                    sx={{ minWidth: isMobile ? '20%' : '20%' }}
                                                />

                                                <TextField
                                                    fullWidth
                                                    variant="filled"
                                                    type="number"
                                                    label={`Crew Set ${index + 1}`}
                                                    name={`crewComplement[${index}].crewSet`}
                                                    value={crew.crewSet}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    error={touched.crewComplement?.[index]?.crewSet && !!errors.crewComplement?.[index]?.crewSet}
                                                    helperText={touched.crewComplement?.[index]?.crewSet && errors.crewComplement?.[index]?.crewSet}
                                                    sx={{ minWidth: isMobile ? '20%' : '20%' }}
                                                />

                                                <FormControlLabel
                                                    sx={{ minWidth: isMobile ? '20%' : '20%' }}
                                                    control={
                                                        <Checkbox
                                                            checked={crew.isCompulsory} // Bind to specific crew item's isCompulsory
                                                            onChange={handleChange}
                                                            name={`crewComplement[${index}].isCompulsory`}
                                                            color="primary"
                                                        />
                                                    }
                                                    label="Is Compulsory"
                                                />
                                            </Box>
                                        </ListItem>
                                    ))}
                                    <ListItem>
                                        <Button 
                                            startIcon={<Add />}
                                            onClick={() => addCrewMember(values, setFieldValue)}
                                        >
                                            Add Crew Member
                                        </Button>
                                    </ListItem>
                                </List>

                                <FormControlLabel
                                    sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}
                                    control={
                                        <Checkbox
                                            checked={values.isActive}
                                            onChange={handleChange}
                                            name="isActive"
                                            color="primary"
                                        />
                                    }
                                    label="Is Active"
                                />
                                
                                <Button
                                    fullWidth
                                    type="submit"
                                    variant="contained"
                                    color="secondary"
                                    sx={{ gridColumn: "span 12", height: 42 }}
                                >
                                    {aircraftId ? "Update" : "Create"}
                                </Button>
                            </Box>
                        </form>
                    )}
                </Formik>
            </Box>
        </Box>
    );
};

export default AircraftPage;
